import { ExecutionCard } from '@src/components/OrderForms/ExecutionCard';
import { FormFooter, OrderFormHeader, OVERLAY_Z_INDEX } from '@src/components/OrderForms/styles';
import {
  AmountInput,
  Box,
  Button,
  ButtonVariants,
  formattedOrderTime,
  FormGroup,
  FormSection,
  Grid,
  HStack,
  IconButton,
  IconName,
  Label,
  MaxAvailable,
  OrderFormSides,
  QuickOptions,
  QuoteStatusEnum,
  resetSwapForm,
  RotatingButton,
  SearchSelect,
  selectCurrenciesIncrement,
  selectEffectiveMaxBalanceArgs,
  selectSecurityAndSide,
  sendLimitOrderThunk,
  setSwapFromCurrency,
  setSwapFromQuantity,
  setSwapStep,
  setSwapToCurrency,
  setSwapToQuantity,
  SideEnum,
  Spinner,
  Stack,
  switchSwapSides,
  useSwapDependencies,
  useSwapQuickButtons,
  useWLEffectiveMaxBalance,
  useWLOrder,
  useWLOrderFormDispatch,
  useWLOrderFormSelector,
} from '@talos/kyoko';
import { AnimatePresence } from 'framer-motion';
import { Fragment, useCallback } from 'react';
import { SpinnerWrapper } from './styles';

export function SwapForm() {
  useSwapDependencies();

  const dispatch = useWLOrderFormDispatch();
  const fromCurrency = useWLOrderFormSelector(state => state.swap.form.fromCurrency);
  const toCurrency = useWLOrderFormSelector(state => state.swap.form.toCurrency);
  const fromQty = useWLOrderFormSelector(state => state.swap.form.fromQty);
  const toQty = useWLOrderFormSelector(state => state.swap.form.toQty);
  const swapStep = useWLOrderFormSelector(state => state.swap.meta.step);
  const quote = useWLOrderFormSelector(state => state.swap.quote);
  const sentOrderID = useWLOrderFormSelector(state => state.swap.meta.order?.OrderID);
  const { fromCurrencyDetails } = useWLOrderFormSelector(selectCurrenciesIncrement);
  const securityAndSide = useWLOrderFormSelector(selectSecurityAndSide);
  const effectiveMaxBalanceArgs = useWLOrderFormSelector(selectEffectiveMaxBalanceArgs);
  const effectiveMax = useWLEffectiveMaxBalance(effectiveMaxBalanceArgs);

  const fromMaxAvailable =
    securityAndSide?.security.BaseCurrency === fromCurrency.value?.Symbol
      ? effectiveMax.effectiveMaxBase
      : securityAndSide?.security.QuoteCurrency === fromCurrency.value?.Symbol
        ? effectiveMax.effectiveMaxQuote
        : undefined;

  const onClickContinueButton = useCallback(async () => {
    if (swapStep === 'idle') {
      dispatch(setSwapStep('locking'));
    }
    if (swapStep === 'locked') {
      await dispatch(sendLimitOrderThunk());
    }
  }, [dispatch, swapStep]);
  const quoteCountdown = useWLOrderFormSelector(state => state.swap.meta.quoteCountdown);

  const { pct, quickButtonItems } = useSwapQuickButtons(fromMaxAvailable, fromCurrencyDetails);

  const order = useWLOrder({ orderID: sentOrderID });
  const onCloseCard = useCallback(() => {
    dispatch(resetSwapForm({}));
  }, [dispatch]);

  return (
    <>
      <Grid gridTemplateRows="auto 1fr auto" h="100%" position="relative">
        <Fragment>
          <AnimatePresence>
            {(swapStep === 'locking' || swapStep === 'sendingEntity' || order) && (
              <SpinnerWrapper
                data-testid="swap-spinner"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {(swapStep === 'locking' || swapStep === 'sendingEntity') && <Spinner />}
              </SpinnerWrapper>
            )}
          </AnimatePresence>
        </Fragment>

        <OrderFormHeader>
          {toCurrency.value?.Symbol && fromCurrency.value?.Symbol && (
            <>
              Exchanging {fromCurrency.value.Description} to {toCurrency.value.Description}
            </>
          )}
        </OrderFormHeader>

        <FormSection>
          <Stack p="spacing8" border="1px solid gray" background="gray.020" borderRadius="borderRadiusDefault">
            <HStack alignItems="center" gap="spacing4">
              <FormGroup
                error={fromQty.hasInvalidValue && fromQty.errorString}
                warning={fromQty.warningString}
                controlId="fromQty"
                labelWidth="100%"
                disabled={fromQty.isDisabled || swapStep === 'locked'}
                mb="spacing24"
                display="flex"
                w="100%"
              >
                <Stack w="100%" gap="spacing4">
                  <HStack w="100%" gap="spacing8">
                    <Stack flex="3" gap="spacing4">
                      <Label htmlFor="fromQty">From</Label>
                      <SearchSelect
                        data-testid="swap-from-currency"
                        getLabel={item => item?.Symbol ?? ''}
                        getDescription={item => item?.Description ?? ''}
                        options={fromCurrency.availableItems}
                        selection={fromCurrency.value}
                        onChange={currency => {
                          dispatch(setSwapFromCurrency(currency));
                        }}
                        disabled={fromCurrency.isDisabled || swapStep === 'locked'}
                      />
                    </Stack>

                    <Stack flex="6" gap="spacing4">
                      <Label htmlFor="fromQty" display="flex" style={{ alignSelf: 'flex-end', height: '12px' }}>
                        {fromMaxAvailable && securityAndSide?.security && securityAndSide.side && (
                          <MaxAvailable
                            maxAvailable={fromMaxAvailable}
                            currency={fromCurrency.value?.Symbol}
                            side={securityAndSide.side === SideEnum.Buy ? OrderFormSides.Sell : OrderFormSides.Buy}
                            estimate={effectiveMax.estimate}
                          />
                        )}
                      </Label>

                      <AmountInput
                        value={fromQty.displayValue ?? ''}
                        touched={fromQty.isTouched}
                        invalid={fromQty.hasInvalidValue}
                        currency={fromCurrency.value?.Symbol}
                        onChange={qty => {
                          dispatch(setSwapFromQuantity({ qty: qty ?? '', setTouched: true }));
                        }}
                        min="0"
                        type="number"
                        name="fromQty"
                        id="fromQty"
                        disabled={fromQty.isDisabled || swapStep === 'locked'}
                        data-testid="swap-from-qty"
                      />
                    </Stack>
                  </HStack>
                  <QuickOptions
                    onOptionClick={v => {
                      v && dispatch(setSwapFromQuantity({ qty: pct[v], setTouched: true }));
                    }}
                    options={quickButtonItems}
                    disabled={fromQty.isDisabled || swapStep === 'locked'}
                  />
                </Stack>
              </FormGroup>
            </HStack>
          </Stack>

          <HStack alignSelf="center" borderRadius="100%" py="spacing24">
            <RotatingButton>
              <IconButton
                data-testid="switch-sides-button"
                icon={IconName.ArrowUpDown}
                variant={ButtonVariants.Primary}
                disabled={swapStep === 'locked'}
                onClick={() => {
                  dispatch(switchSwapSides());
                }}
                style={{ borderRadius: '100%' }}
              />
            </RotatingButton>
          </HStack>

          <Stack p="spacing8" border="1px solid gray" background="gray.020" borderRadius="borderRadiusDefault">
            <HStack alignItems="center" gap="spacing4">
              <FormGroup
                error={toQty.hasInvalidValue && toQty.errorString}
                warning={toQty.warningString}
                controlId="toQty"
                labelWidth="100%"
                disabled={toQty.isDisabled || swapStep === 'locked'}
                mb="spacing24"
                display="flex"
                w="100%"
              >
                <Stack w="100%" gap="spacing4">
                  <HStack w="100%" gap="spacing8">
                    <Stack flex="3" gap="spacing4">
                      <Label htmlFor="toQty">To</Label>
                      <SearchSelect
                        getLabel={item => item?.Symbol ?? ''}
                        getDescription={item => item?.Description ?? ''}
                        options={toCurrency.availableItems}
                        selection={toCurrency.value}
                        onChange={currency => {
                          dispatch(setSwapToCurrency(currency));
                        }}
                        disabled={toCurrency.isDisabled || swapStep === 'locked'}
                        data-testid="swap-to-currency"
                      />
                    </Stack>

                    <Stack flex="6" gap="spacing4" mt="16px">
                      <AmountInput
                        value={toQty.displayValue ?? ''}
                        touched={toQty.isTouched}
                        invalid={toQty.hasInvalidValue}
                        currency={toCurrency.value?.Symbol}
                        onChange={qty => {
                          dispatch(setSwapToQuantity({ qty: qty ?? '', setTouched: true }));
                        }}
                        min="0"
                        type="number"
                        name="toQty"
                        id="toQty"
                        disabled={toQty.isDisabled || swapStep === 'locked'}
                        data-testid="swap-to-qty"
                      />
                    </Stack>
                  </HStack>
                </Stack>
              </FormGroup>
            </HStack>
          </Stack>
        </FormSection>
        <FormFooter>
          <Button
            data-testid="swap-clear-button"
            variant={ButtonVariants.Default}
            onClick={() => {
              dispatch(resetSwapForm({}));
            }}
          >
            Clear
          </Button>

          <Button
            data-testid="swap-button"
            variant={ButtonVariants.Primary}
            onClick={onClickContinueButton}
            disabled={!quote || quote.QuoteStatus === QuoteStatusEnum.Rejected}
          >
            {quoteCountdown ? `Convert (${formattedOrderTime(quoteCountdown * 1000)})` : 'Convert'}
          </Button>
        </FormFooter>
      </Grid>
      {order && (
        <Box zIndex={OVERLAY_Z_INDEX + 1}>
          <ExecutionCard
            attached={true}
            order={order}
            onCloseCard={onCloseCard}
            onCancelOrder={null}
            onArchiveOrder={null}
            onResubmitOrder={null}
            onModifyOrder={null}
            showSubmitTime
          />
        </Box>
      )}
    </>
  );
}
